/**
 * Lists in the mail builder's text block (board wave 01.11., L-29).
 *
 * The editor stores real `<ul>` / `<ol>` (that is what `execCommand` makes and
 * what the canvas draws). A mail cannot send them as they are: Outlook's Word
 * engine gives a list its own indent and spacing, and the plain-text part
 * (`emailHtmlToText`) prints every item as "- ", numbers lost. So the export
 * turns each list into a presentation table — one row per item, a marker cell
 * (`•` / `1.`) and a text cell — which every client lays out alike and the
 * plain text reads as "• text" / "1. text" (one row = one line).
 *
 * Both functions work on the parsed DOM and never on the markup string, like
 * `stripReservedClasses`; without a DOM (never on the send path) the markup
 * is returned untouched. HTML without a list comes back as the very same
 * string — the contract that keeps every mail stored so far byte-identical.
 */
import { AUTHORED_CLOSE, AUTHORED_OPEN, markAuthored } from "./export-compact";

/** What the export knows about the text block the list sits in. */
export type ListTableContext = {
  /** The direction of the text (W-A.6): the table runs that way, the cells align to its start. */
  dir: "ltr" | "rtl";
  /** The block's font size — the marker column's width follows it. */
  fontSizePx: number;
  /**
   * The text declarations of the block's text element (colour, font, size,
   * weight, style, spacing, transform, leading, direction), joined by `;`.
   * Both cells carry them: a table inherits no font in Outlook.
   */
  textStyle: string;
  /**
   * The classes both cells carry (the phone size step-down and the mobile font
   * size — never the mobile ALIGNMENT, a list stays aligned to its start). A
   * FUNCTION, called only when the text has a list: asking for a mobile class
   * registers a CSS rule and moves the `vt-mN` numbering of every later block.
   */
  textClass: () => string;
};

const HAS_LIST = /<(ul|ol)\b/i;
const LIST_SELECTOR = "ul,ol";
/** Block elements inside an item: each becomes a line of it, separated by `<br>`. */
const ITEM_BLOCKS = "div,p";
/** A pass per nesting level that survives flattening (a list inside an inline element of an item). */
const MAX_PASSES = 32;

const isList = (node: Node | null): node is HTMLElement =>
  node !== null && node.nodeType === Node.ELEMENT_NODE && /^(UL|OL)$/.test((node as Element).tagName);
/** Elements that start a line of their own — no `<br>` needed next to them. */
const BLOCK_TAG = /^(DIV|P|UL|OL|LI|TABLE|H[1-6]|BLOCKQUOTE|PRE|HR)$/;
const isBlock = (node: Node): boolean => node.nodeType === Node.ELEMENT_NODE && BLOCK_TAG.test((node as Element).tagName);
const isBr = (node: Node | null): boolean =>
  node !== null && node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "BR";
/** A text node with nothing to show (a no-break space counts as nothing too). */
const isBlank = (node: Node): boolean => node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim() === "";
/** Source formatting only: spaces, tabs, line breaks — not a no-break space. */
const isLayoutWhitespace = (node: Node): boolean =>
  node.nodeType === Node.TEXT_NODE && /^[ \t\r\n\f]*$/.test(node.textContent ?? "");

/**
 * The export's list → table step, for the authored html of one Paragraph
 * (after `authoredHtml`, so the classes added here are not stripped as
 * reserved). Keeps the compactor's markers around the result.
 */
export function listsToTables(html: string, ctx: ListTableContext): string {
  if (!HAS_LIST.test(html) || typeof document === "undefined") return html;
  const marked = html.length >= 2 && html.startsWith(AUTHORED_OPEN) && html.endsWith(AUTHORED_CLOSE);
  const template = parse(marked ? html.slice(1, -1) : html);
  if (!template.content.querySelector(LIST_SELECTOR)) return html;

  let cellClass: string | undefined;
  const classOnce = () => (cellClass ??= ctx.textClass());
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const lists = outermostLists(template.content);
    if (lists.length === 0) break;
    for (const list of lists) {
      const { items, dropped } = withoutEmptyEnds(collectItems(list));
      const replacement = items.length > 0 ? buildTable(list, items, dropped, ctx, classOnce) : null;
      replaceList(list, replacement);
    }
  }
  const out = template.innerHTML;
  return marked ? markAuthored(out) : out;
}

/**
 * The paste path's list cleanup (`sanitizePastedHtml(…, { keepLists })`): one
 * flat level, a `div` / `p` inside an item becomes a line break, empty items
 * and lists go, and the `<br>` that only ends the line in front of a list goes
 * (`replaceList`). The result is what the editor itself would make; the export
 * still does not rely on it.
 */
export function normalizeListsHtml(html: string): string {
  if (!HAS_LIST.test(html) || typeof document === "undefined") return html;
  const template = parse(html);
  if (!template.content.querySelector(LIST_SELECTOR)) return html;
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const lists = outermostLists(template.content).filter((l) => !l.hasAttribute("data-flat"));
    if (lists.length === 0) break;
    for (const list of lists) {
      const items = collectItems(list).filter((item) => !isEmptyItem(item));
      let flat: HTMLElement | null = null;
      if (items.length > 0) {
        flat = document.createElement(list.tagName.toLowerCase());
        flat.setAttribute("data-flat", "");
        for (const item of items) {
          const li = document.createElement("li");
          li.appendChild(item);
          flat.appendChild(li);
        }
      }
      replaceList(list, flat);
    }
  }
  for (const flat of template.content.querySelectorAll("[data-flat]")) flat.removeAttribute("data-flat");
  return template.innerHTML;
}

function parse(html: string): HTMLTemplateElement {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template;
}

function outermostLists(root: DocumentFragment): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(LIST_SELECTOR)).filter(
    (list) => !list.parentElement?.closest(LIST_SELECTOR),
  );
}

/**
 * Swaps the list for its table (or flat list). The canvas is the measure
 * (Chromium, audit r1 L-M4): the ONE `<br>` that ends a line of content right
 * before a list draws nothing there (the list is a block) but is an empty
 * line in Outlook — it goes. Two or more `<br>` before the list, a `<br>`
 * after a block or at the very start, and every `<br>` right after the list
 * are empty lines in the canvas — they stay. A list with nothing in it just
 * goes, its neighbours' line breaks kept; between two runs of inline content
 * it leaves one `<br>`, or the lines around it would glue together.
 */
function replaceList(list: HTMLElement, replacement: HTMLElement | null) {
  const parent = list.parentNode;
  if (!parent) return;
  if (!replacement) {
    const prev = adjacentContent(list, "previousSibling");
    const next = adjacentContent(list, "nextSibling");
    const between = prev && next && !isBr(prev) && !isBr(next) && !isBlock(prev) && !isBlock(next);
    if (between) parent.replaceChild(document.createElement("br"), list);
    else parent.removeChild(list);
    return;
  }
  const before = lineEndBefore(list);
  parent.replaceChild(replacement, list);
  before?.parentNode?.removeChild(before);
}

/** The nearest sibling that is not source formatting. */
function adjacentContent(node: Node, side: "previousSibling" | "nextSibling"): Node | null {
  let sibling = node[side];
  while (sibling && isLayoutWhitespace(sibling)) sibling = sibling[side];
  return sibling;
}

/** The `<br>` right before `list` when it only ends a line of content (see `replaceList`). */
function lineEndBefore(list: Node): Node | null {
  const br = adjacentContent(list, "previousSibling");
  if (!br || !isBr(br)) return null;
  const line = adjacentContent(br, "previousSibling");
  return line && rendersInline(line) ? br : null;
}

/** Inline content that draws something: text beyond layout whitespace, or an inline element holding such text or an image. */
function rendersInline(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) return !isLayoutWhitespace(node);
  if (node.nodeType !== Node.ELEMENT_NODE || isBlock(node) || isBr(node)) return false;
  const el = node as Element;
  return el.tagName === "IMG" || /[^ \t\r\n\f]/.test(el.textContent ?? "") || el.querySelector("img") !== null;
}

/**
 * The items of a list, flattened into one level in reading order: an item's
 * own lines, then the items of a list nested in it, then what follows that
 * list inside the item. Each item is a fragment with its lines joined by
 * `<br>`. An item with nothing in it (`<li><br></li>`) is kept, empty — the
 * canvas draws it with its marker and number (audit r1 L-L1); the callers
 * decide what to do with it. The empty run before / after a list nested in an
 * item is no item.
 */
function collectItems(list: HTMLElement): DocumentFragment[] {
  const items: DocumentFragment[] = [];
  let loose: Node[] = [];
  const flushLoose = () => {
    if (loose.length > 0) pushItem(items, loose, false);
    loose = [];
  };
  for (const child of Array.from(list.childNodes)) {
    if (isList(child)) {
      flushLoose();
      items.push(...collectItems(child));
    } else if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === "LI") {
      flushLoose();
      const parts = Array.from(child.childNodes);
      const nested = parts.some(isList);
      let own: Node[] = [];
      for (const part of parts) {
        if (isList(part)) {
          pushItem(items, own, false);
          own = [];
          items.push(...collectItems(part));
        } else {
          own.push(part);
        }
      }
      pushItem(items, own, !nested);
    } else if (isBr(child)) {
      flushLoose();
    } else if (!isBlank(child)) {
      // Content straight inside the list (not valid html, but stored html is not
      // always valid): an item of its own.
      loose.push(child);
    }
  }
  flushLoose();
  return items;
}

function pushItem(items: DocumentFragment[], nodes: Node[], keepEmpty: boolean) {
  const fragment = document.createDocumentFragment();
  for (const node of nodes) fragment.appendChild(node);
  blocksToBreaks(fragment);
  trimEdges(fragment);
  if (keepEmpty || !isEmptyItem(fragment)) items.push(fragment);
}

/** An item with nothing to show: no text beyond whitespace, no image. */
function isEmptyItem(item: DocumentFragment): boolean {
  return (item.textContent ?? "").trim() === "" && !item.querySelector("img");
}

/**
 * The export keeps an empty item between two others as a row, so the numbers
 * after it match the canvas; at the start or the end of a list it goes (the
 * empty item Enter leaves behind before the list is left). `dropped`: how many
 * went at the start — the canvas still counts them, so a numbered list starts
 * that much later (audit r2 E2).
 */
function withoutEmptyEnds(items: DocumentFragment[]): { items: DocumentFragment[]; dropped: number } {
  let start = 0;
  let end = items.length;
  while (start < end && isEmptyItem(items[start] as DocumentFragment)) start++;
  while (end > start && isEmptyItem(items[end - 1] as DocumentFragment)) end--;
  return { items: items.slice(start, end), dropped: start };
}

/**
 * `<li><div>a</div><div>b</div></li>` → `a<br>b`: a block inside an item is
 * unwrapped, with a `<br>` where it met other content. Unwrapping alone would
 * glue the lines together. Innermost first, so nested blocks unwrap cleanly.
 */
function blocksToBreaks(fragment: DocumentFragment) {
  const blocks = Array.from(fragment.querySelectorAll(ITEM_BLOCKS)).reverse();
  for (const block of blocks) {
    const parent = block.parentNode;
    if (!parent) continue;
    const prev = meaningfulSibling(block, "previousSibling");
    const next = meaningfulSibling(block, "nextSibling");
    if (prev && !isBr(prev)) parent.insertBefore(document.createElement("br"), block);
    const endsInBr = isBr(block.lastChild);
    while (block.firstChild) parent.insertBefore(block.firstChild, block);
    if (next && !endsInBr && !isBr(next)) parent.insertBefore(document.createElement("br"), block);
    parent.removeChild(block);
  }
}

function meaningfulSibling(node: Node, side: "previousSibling" | "nextSibling"): Node | null {
  let sibling = node[side];
  while (sibling && isBlank(sibling)) sibling = sibling[side];
  return sibling;
}

/** Leading / trailing `<br>` and layout whitespace of an item go. */
function trimEdges(fragment: DocumentFragment) {
  while (fragment.firstChild && (isBr(fragment.firstChild) || isBlank(fragment.firstChild))) {
    fragment.removeChild(fragment.firstChild);
  }
  while (fragment.lastChild && (isBr(fragment.lastChild) || isBlank(fragment.lastChild))) {
    fragment.removeChild(fragment.lastChild);
  }
  const first = fragment.firstChild;
  if (first?.nodeType === Node.TEXT_NODE) first.textContent = (first.textContent ?? "").replace(/^[ \t\r\n]+/, "");
  const last = fragment.lastChild;
  if (last?.nodeType === Node.TEXT_NODE) last.textContent = trimLayoutEnd(last.textContent ?? "");
}

/**
 * `text` without its trailing spaces, tabs and line breaks. A loop, not
 * `/[ \t\r\n]+$/`: that backtracks quadratically on a long run of spaces
 * inside the text (60 000 spaces: 7 s and more — audit r1 L-L2).
 */
function trimLayoutEnd(text: string): string {
  let end = text.length;
  for (;;) {
    const c = text[end - 1];
    if (c !== " " && c !== "\t" && c !== "\r" && c !== "\n") break;
    end--;
  }
  return end === text.length ? text : text.slice(0, end);
}

/** The highest number an ordered list starts at (5 digits: the marker column stays narrow). */
const MAX_START = 99999;

/** The marker column's width: the widest marker at this font size (a digit or `.` ≈ 0.6 em, plus slack). */
const markerWidthPx = (fontSizePx: number, chars: number) => Math.ceil((fontSizePx * (2 + 6 * chars)) / 10);

function buildTable(
  list: HTMLElement,
  items: DocumentFragment[],
  dropped: number,
  ctx: ListTableContext,
  cellClass: () => string,
): HTMLTableElement {
  const ordered = list.tagName === "OL";
  const startAttr = Number.parseInt(list.getAttribute("start") ?? "", 10);
  // The canvas's number of the first row kept: `start` (1 without one) plus
  // the empty items dropped before it (`withoutEmptyEnds`, audit r2 E2). A
  // whole number from 0 to MAX_START (audit r1 L-L3): a huge `start` printed
  // "1.23e+29." in a 224 px marker column.
  const first = (Number.isFinite(startAttr) ? startAttr : 1) + dropped;
  const start = ordered ? Math.min(MAX_START, Math.max(0, first)) : 1;
  const markers = items.map((_, i) => (ordered ? `${start + i}.` : "\u2022"));
  const fontSizePx = Number.isFinite(ctx.fontSizePx) && ctx.fontSizePx > 0 ? ctx.fontSizePx : 16;
  const width = markerWidthPx(fontSizePx, Math.max(...markers.map((m) => m.length)));
  const gap = Math.round(fontSizePx / 2);
  const rtl = ctx.dir === "rtl";
  const align = rtl ? "right" : "left";
  const classes = cellClass();
  const style = (...declarations: string[]) => [ctx.textStyle, ...declarations].filter((d) => d !== "").join(";");

  const table = document.createElement("table");
  table.setAttribute("role", "presentation");
  table.setAttribute("width", "100%");
  table.setAttribute("cellpadding", "0");
  table.setAttribute("cellspacing", "0");
  table.setAttribute("border", "0");
  table.setAttribute("style", "border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt");
  if (rtl) table.setAttribute("dir", "rtl");

  items.forEach((item, i) => {
    const tr = document.createElement("tr");
    const marker = document.createElement("td");
    marker.setAttribute("valign", "top");
    marker.setAttribute("align", align);
    marker.setAttribute("width", String(width));
    if (classes) marker.setAttribute("class", classes);
    marker.setAttribute(
      "style",
      style(`width:${width}px`, "white-space:nowrap", `text-align:${align}`, rtl ? `padding:0 0 0 ${gap}px` : `padding:0 ${gap}px 0 0`),
    );
    marker.textContent = markers[i] ?? "";
    const text = document.createElement("td");
    text.setAttribute("valign", "top");
    text.setAttribute("align", align);
    if (classes) text.setAttribute("class", classes);
    text.setAttribute("style", style(`text-align:${align}`));
    // An empty item between two others (`withoutEmptyEnds`): a no-break space
    // keeps its row a line high in every client.
    if (isEmptyItem(item)) text.textContent = "\u00a0";
    else text.appendChild(item);
    tr.appendChild(marker);
    tr.appendChild(text);
    table.appendChild(tr);
  });
  return table;
}
